import {
  JSNS,
  type AccountACEInfo,
  type AccountInfo,
  type AccountInfoPublic,
  type CertificateInfo,
  type CheckRightsTargetInfo,
  type CheckRightsTargetSpec,
  type Content,
  type DiscoverRightsInfo,
  type HABGroup,
  type JsonObject,
  type Prop,
  type SMIMEPublicCertInfo,
  type VersionInfo,
} from '@zimbra-webif/types';
import type { ZimbraClient } from '../client';

function asArray<T>(v: T[] | T | undefined): T[] {
  if (v === undefined) return [];
  return Array.isArray(v) ? v : [v];
}

function content(value: unknown): string | undefined {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return (value[0] as Content | undefined)?._content;
  if (value && typeof value === 'object' && '_content' in value) {
    return String((value as Content)._content);
  }
  return undefined;
}

/** GetInfo section tokens (canonical per Zimbra docs; the attr is free-form). */
export type GetInfoSection =
  | 'mbox'
  | 'prefs'
  | 'attrs'
  | 'zimlets'
  | 'props'
  | 'idents'
  | 'sigs'
  | 'dsrcs'
  | 'children';

/** Account selector (`<account by=...>value</account>`). */
export interface AccountSelectorInput {
  by?: 'name' | 'id' | 'adminName' | 'appAdminName' | 'krb5Principal';
  value: string;
}

/**
 * Account info, server/version info, properties, rights/ACL, S/MIME, OAuth,
 * GCM/FCM, and the HAB tree (urn:zimbraAccount).
 *
 * One public method per SOAP command; see docs/features/account-prefs-security.md.
 */
export class AccountApi {
  constructor(private readonly client: ZimbraClient) {}

  /* ---------------------------------------------------------------------- */
  /* Account / version / client info                                         */
  /* ---------------------------------------------------------------------- */

  /**
   * GetInfoRequest — the bootstrap payload. `sections` limits the payload
   * (comma-joined on the wire); `rights` also resolves those rights inline.
   *
   * NOTE: the `sections` token vocabulary is canonical-per-docs, not binary
   * verified (doc Open #2). The full response is returned typed-but-open.
   */
  async getInfo(sections?: GetInfoSection[], rights?: string[]): Promise<AccountInfo> {
    const params: JsonObject = {};
    if (sections && sections.length > 0) params.sections = sections.join(',');
    if (rights && rights.length > 0) params.rights = rights.join(',');
    return this.client.call<AccountInfo>(JSNS.account, 'GetInfo', params);
  }

  /** GetAccountInfoRequest — public-ish info about an arbitrary account. */
  async getAccountInfo(account: AccountSelectorInput): Promise<AccountInfoPublic> {
    return this.client.call<AccountInfoPublic>(JSNS.account, 'GetAccountInfo', {
      account: { by: account.by ?? 'name', _content: account.value },
    });
  }

  /** GetVersionInfoRequest — server version/build for the about box. */
  async getVersionInfo(): Promise<VersionInfo> {
    const raw = await this.client.call<{ info?: VersionInfo[] | VersionInfo }>(
      JSNS.account,
      'GetVersionInfo',
      {},
    );
    return asArray(raw.info)[0] ?? {};
  }

  /**
   * ClientInfoRequest — per-domain pre-login branding (`<a name>value</a>` bag),
   * keyed by virtual host. Returns a flat `Record<string,string>`.
   */
  async clientInfo(domain: { by?: 'name' | 'id' | 'virtualHostname'; value: string }): Promise<Record<string, string>> {
    const raw = await this.client.call<{ a?: Array<{ name?: string } & Content> | ({ name?: string } & Content) }>(
      JSNS.account,
      'ClientInfo',
      { domain: { by: domain.by ?? 'name', _content: domain.value } },
    );
    const out: Record<string, string> = {};
    for (const a of asArray(raw.a)) {
      if (a.name && a._content !== undefined) out[a.name] = String(a._content);
    }
    return out;
  }

  /* ---------------------------------------------------------------------- */
  /* Properties (per-zimlet user props)                                      */
  /* ---------------------------------------------------------------------- */

  /**
   * ModifyPropertiesRequest — write per-zimlet user properties (a free-form
   * `(zimlet, name)=value` store, distinct from prefs; read back via GetInfo
   * `<props>`).
   */
  async modifyProperties(props: Array<Pick<Prop, 'zimlet' | 'name'> & { value: string }>): Promise<JsonObject> {
    return this.client.call(JSNS.account, 'ModifyProperties', {
      prop: props.map((p) => ({ zimlet: p.zimlet, name: p.name, _content: p.value })),
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Rights / delegated access                                               */
  /* ---------------------------------------------------------------------- */

  /** GetRightsRequest — list granted ACEs (optionally filtered by right name). */
  async getRights(rights?: string[]): Promise<AccountACEInfo[]> {
    const params: JsonObject = {};
    if (rights && rights.length > 0) params.ace = rights.map((right) => ({ right }));
    const raw = await this.client.call<{ ace?: AccountACEInfo[] | AccountACEInfo }>(
      JSNS.account,
      'GetRights',
      params,
    );
    return asArray(raw.ace);
  }

  /** GrantRightsRequest — grant the given ACEs on this account. */
  async grantRights(aces: AccountACEInfo[]): Promise<AccountACEInfo[]> {
    const raw = await this.client.call<{ ace?: AccountACEInfo[] | AccountACEInfo }>(
      JSNS.account,
      'GrantRights',
      { ace: aces },
    );
    return asArray(raw.ace);
  }

  /** RevokeRightsRequest — revoke the given ACEs from this account. */
  async revokeRights(aces: AccountACEInfo[]): Promise<AccountACEInfo[]> {
    const raw = await this.client.call<{ ace?: AccountACEInfo[] | AccountACEInfo }>(
      JSNS.account,
      'RevokeRights',
      { ace: aces },
    );
    return asArray(raw.ace);
  }

  /** CheckRightsRequest — test specific rights against targets. */
  async checkRights(targets: CheckRightsTargetSpec[]): Promise<CheckRightsTargetInfo[]> {
    const raw = await this.client.call<{ target?: CheckRightsTargetInfo[] | CheckRightsTargetInfo }>(
      JSNS.account,
      'CheckRights',
      {
        target: targets.map((t) => ({
          type: t.type,
          by: t.by,
          key: t.key,
          right: t.right.map((r) => ({ _content: r })),
        })),
      },
    );
    return asArray(raw.target);
  }

  /** DiscoverRightsRequest — what THIS user has been granted on others. */
  async discoverRights(rights: string[]): Promise<DiscoverRightsInfo[]> {
    const raw = await this.client.call<{ targets?: DiscoverRightsInfo[] | DiscoverRightsInfo }>(
      JSNS.account,
      'DiscoverRights',
      { right: rights.map((r) => ({ _content: r })) },
    );
    return asArray(raw.targets);
  }

  /* ---------------------------------------------------------------------- */
  /* S/MIME                                                                  */
  /* ---------------------------------------------------------------------- */

  /**
   * GetSMIMEPublicCertsRequest — look up a correspondent's public certs.
   * `store`/`source` lookup options are passed through to the `<store>` spec.
   */
  async getPublicCerts(
    email: string,
    store?: { storeLookupOpt?: string; sourceLookupOpt?: string },
  ): Promise<Array<{ email?: string; cert?: SMIMEPublicCertInfo[] } & JsonObject>> {
    const params: JsonObject = { email: { _content: email } };
    if (store) params.store = { ...store };
    const raw = await this.client.call<{ certs?: Array<JsonObject> | JsonObject }>(
      JSNS.account,
      'GetSMIMEPublicCerts',
      params,
    );
    return asArray(raw.certs) as Array<{ email?: string; cert?: SMIMEPublicCertInfo[] } & JsonObject>;
  }

  /** GetSmimeCertificateInfoRequest — the user's own cert(s) (omit certId = all). */
  async getMyCertificates(certId?: string): Promise<CertificateInfo[]> {
    const params: JsonObject = {};
    if (certId !== undefined) params.certId = certId;
    const raw = await this.client.call<{ certificate?: CertificateInfo[] | CertificateInfo }>(
      JSNS.account,
      'GetSmimeCertificateInfo',
      params,
    );
    return asArray(raw.certificate);
  }

  /**
   * SaveSmimeCertificateRequest — upload a new cert/keypair (referenced by a
   * prior file upload id); `password` is the key passphrase, `replaceId`
   * replaces an existing cert.
   */
  async saveCertificate(opts: {
    uploadId: string;
    password?: string;
    replaceId?: string;
  }): Promise<JsonObject> {
    const params: JsonObject = { upload: { id: opts.uploadId } };
    if (opts.password !== undefined) params.password = opts.password;
    if (opts.replaceId !== undefined) params.replaceId = opts.replaceId;
    return this.client.call(JSNS.account, 'SaveSmimeCertificate', params);
  }

  /* ---------------------------------------------------------------------- */
  /* Misc                                                                    */
  /* ---------------------------------------------------------------------- */

  /** GetGcmSenderIdRequest — GCM/FCM sender id for mobile push registration. */
  async getGcmSenderId(): Promise<string | undefined> {
    const raw = await this.client.call<{ gcmSenderId?: Array<Content> | string }>(
      JSNS.account,
      'GetGcmSenderId',
      {},
    );
    return content(raw.gcmSenderId);
  }

  /** GetHABRequest — the org hierarchical-address-book tree. */
  async getHAB(rootGroupId: string): Promise<HABGroup[]> {
    const raw = await this.client.call<{ habGroups?: HABGroup[] | HABGroup }>(JSNS.account, 'GetHAB', {
      habRootGroupId: rootGroupId,
    });
    return asArray(raw.habGroups);
  }
}
